import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Header from 'components/Checkout2/UI/Header';
import { FunctionComponent, useMemo, useState } from 'react';
import Footer from './Footer';
import Step from './Step';
import { Box, Typography } from '@material-ui/core';
import InfoAlert from './InfoAlert';
import Pet from '@spike/pet-model';
import { SellInfo } from './SellMembership';
import cloneDeep from 'lodash/cloneDeep';
import SelectMultiplePets from './SelectMultiplePets';
import PetInfo from 'components/OrderDetail/PetDetail/PetInfo';
import { Membership } from '@spike/membership-model';
import { SelectMembership } from './SelectMembership';
import Title from './Title';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBill } from '@fortawesome/pro-regular-svg-icons/faMoneyBill';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons/faArrowsRotate';
import Details from './Details';

export interface EditStepProps {
    pets: Array<Pet>;
    sellInfo: SellInfo;
    memberships: Array<Membership>;
    onClose: () => void;
    onBack: () => void;
    onSave: (sellInfo: SellInfo) => void;
    onAddPets: () => void;
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        addPetLink: {
            color: '#92B4A7',
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            marginTop: 20
        },
        title: {
            marginTop: 30
        },
        selectMultiplePets: {
            marginTop: 15
        },
        petInfo: {
            marginTop: 30
        },
        membershipContainer: {
            borderTop: '1px solid #D4D4D4',
            marginTop: 30
        },
        selectMembership: {
            marginTop: 15
        },
        row: {
            display: 'flex',
            alignItems: 'center'
        },
        cell: {
            display: 'flex',
            alignItems: 'center'
        },
        icon: {
            marginRight: 7
        },
        type: {
            fontSize: 14,
            textTransform: 'capitalize'
        },
        typeContainer: {
            display: 'flex',
            alignItems: 'center',
            marginTop: 10
        },
        membershipTitleContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            width: '100%'
        }
    })
);

export const EditStep: FunctionComponent<EditStepProps> = props => {
    const classes = useStyles(props);

    const [editSellInfo, setEditSellInfo] = useState(cloneDeep(props.sellInfo));
    const selectedMembership = useMemo(
        () => props.memberships.find(membership => membership.id === editSellInfo.membershipId),
        [editSellInfo]
    );

    const hasPets = props.pets.length > 0;

    const alertMessage = hasPets ? 'All selected pets are eligible to use the membership.' : 'This client has no pets.';

    const addPetLink = (
        <Box onClick={props.onAddPets}>
            <Typography className={classes.addPetLink}>+ Add Pet</Typography>
        </Box>
    );

    const selectedPets = useMemo(
        () => props.pets.filter(pet => editSellInfo.petIds.includes(pet.id ?? 0)),
        [props.pets, editSellInfo]
    );

    const deletePet = (petId: number) =>
        setEditSellInfo(prev => ({ ...prev, petIds: prev.petIds.filter(id => id !== petId) }));

    const selectMembershipHandler = (membershipId: number) => {
        setEditSellInfo(prev => ({ ...prev, membershipId }));
    };

    const header = (
        <Header
            title="Select Pet(s) for Membership"
            onClose={props.onClose}
            onBack={props.onBack}
        />
    );

    const body = (
        <>
            <InfoAlert>{alertMessage}</InfoAlert>
            {!hasPets ? (
                addPetLink
            ) : (
                <>
                    <Title className={classes.title}>Pets</Title>
                    <SelectMultiplePets
                        pets={props.pets}
                        selectedPetIds={editSellInfo.petIds}
                        onSelectionChange={petIds => setEditSellInfo(prev => ({ ...prev, petIds: [...petIds] }))}
                        className={classes.selectMultiplePets}
                    />
                    <Box>
                        {selectedPets.map(pet => (
                            <PetInfo
                                key={pet.id}
                                pet={pet}
                                onDelete={() => deletePet(pet.id ?? 0)}
                                className={classes.petInfo}
                            />
                        ))}
                    </Box>
                    {selectedPets.length > 0 && (
                        <Box className={classes.membershipContainer}>
                            <Box className={classes.membershipTitleContainer}>
                                <Title className={classes.title}>Membership</Title>
                                {editSellInfo.membershipId && <Details membership={selectedMembership} />}
                            </Box>
                            <SelectMembership
                                memberships={props.memberships}
                                selectedMembershipId={editSellInfo.membershipId}
                                onSelect={selectMembershipHandler}
                                placeholder="Select a Membership"
                                className={classes.selectMembership}
                            />
                        </Box>
                    )}
                    {editSellInfo.membershipId && (
                        <Box className={classes.row}>
                            <Box>
                                <Title className={classes.title}>Membership Type</Title>
                                <Box className={classes.typeContainer}>
                                    <FontAwesomeIcon
                                        icon={
                                            selectedMembership?.enrollmentType === 'package'
                                                ? faMoneyBill
                                                : faArrowsRotate
                                        }
                                        className={classes.icon}
                                    />
                                    <Typography className={classes.type}>
                                        {selectedMembership?.enrollmentType}
                                    </Typography>
                                </Box>
                            </Box>
                            {selectedMembership?.enrollmentType === 'subscription' && (
                                <Box style={{ marginLeft: 50 }}>
                                    <Title className={classes.title}>Billing Frequency</Title>
                                    <Box className={classes.typeContainer}>
                                        <Typography className={classes.type}>
                                            {(selectedMembership?.billingFrequency ?? '') === ''
                                                ? '-'
                                                : selectedMembership?.billingFrequency}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                    {selectedMembership?.enrollmentType === 'subscription' && (
                        <Box className={classes.row}>
                            <Box>
                                <Title className={classes.title}>End Date</Title>
                                <Box className={classes.typeContainer}>
                                    <Typography className={classes.type}>Until Canceled</Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </>
            )}
        </>
    );

    const footer = (
        <Footer
            total={Number(selectedMembership?.total ?? 0)}
            buttonLabel="Save"
            disableButton={editSellInfo.petIds.length === 0 || editSellInfo.membershipId === undefined}
            onClickButton={() => props.onSave(editSellInfo)}
        />
    );

    return (
        <Step
            header={header}
            body={body}
            footer={footer}
        />
    );
};

export default EditStep;
